import React, { useState } from 'react';
import { Form, FormControl, Row, Col } from 'react-bootstrap';
import { Field } from 'formik';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { logInfo } from '../../config_and_helpers/logger';

/**
 * OrderItemKitComponentLotComponent
 *
 * Represents a component for displaying and editing lot details for individual kit components within an order item.
 * Handles individual lot tracking including batch numbers, quantities, expiry dates, and storage locations for each kit component.
 *
 * @param {Object} props - The component props
 * @param {number} props.orderItemIndex - The index of the parent order item in the order items array
 * @param {number} props.kitComponentIndex - The index of the kit component within the kit components array
 * @param {number} props.lotIndex - The index of this lot item within the kit component's lot items array
 * @param {Object} props.kitComponent - The kit component object containing component details
 * @param {Object} props.formik - Formik object containing form state, validation, and helper methods
 *
 * @returns {JSX.Element} A form section for managing a single lot item entry for a kit component
 */
const OrderItemKitComponentLotComponent = ({
  orderItemIndex,
  kitComponentIndex,
  lotIndex,
  kitComponent,
  formik,
}) => {
  // Field names for Formik form fields
  const batchFieldName = `items[${orderItemIndex}].kitComponentLots[${kitComponentIndex}].lots[${lotIndex}].batch`;
  const expiryFieldName = `items[${orderItemIndex}].kitComponentLots[${kitComponentIndex}].lots[${lotIndex}].expiry`;
  const storageLocationFieldName = `items[${orderItemIndex}].kitComponentLots[${kitComponentIndex}].lots[${lotIndex}].storageLocation`;
  const [expiryInputValue, setExpiryInputValue] = useState('');
  const [isExpiryInputDirty, setIsExpiryInputDirty] = useState(false);

  const formatIsoToDisplay = isoDateString => {
    if (!isoDateString || !/^\d{4}-\d{2}-\d{2}$/.test(isoDateString)) return '';
    const [year, month, day] = isoDateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const convertDateToIso = date => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseManualDateToIso = rawValue => {
    const trimmed = (rawValue || '').trim();
    if (trimmed === '') return '';
    const match = trimmed.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (!match) return null;
    const [, dayRaw, monthRaw, yearRaw] = match;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const normalizedDate = new Date(year, month - 1, day);
    const isValid =
      normalizedDate.getFullYear() === year &&
      normalizedDate.getMonth() === month - 1 &&
      normalizedDate.getDate() === day;
    if (!isValid) return null;
    return `${yearRaw}-${monthRaw}-${dayRaw}`;
  };

  return (
    <div className="order-lot-panel order-lot-panel--compact">
      {/* TEST DIV - This should be visible */}

      <div className="mb-3">
        <h6 className="mb-0">Batch Details</h6>
      </div>

      <Row>
        <Col md={6}>
          {/* Batch Number Field */}
          <Form.Group
            controlId={`kitComponentBatch${kitComponentIndex}${lotIndex}`}
            className="field-margin"
          >
            <Form.Label>Batch Number</Form.Label>
            <Field name={batchFieldName}>
              {({ field, meta }) => {
                return (
                  <FormControl
                    {...field}
                    type="text"
                    isValid={meta.touched && !meta.error && field.value !== ''}
                    isInvalid={meta.touched && !!meta.error}
                  />
                );
              }}
            </Field>
            <Form.Control.Feedback type="invalid">
              {!!formik?.errors?.items?.[orderItemIndex]?.kitComponentLots?.[
                kitComponentIndex
              ]?.lots?.[lotIndex]?.batch &&
                formik.errors.items?.[orderItemIndex]?.kitComponentLots?.[
                  kitComponentIndex
                ]?.lots?.[lotIndex]?.batch}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          {/* Expiry Date Field */}
          <Form.Group
            controlId={`kitComponentExpiry${kitComponentIndex}${lotIndex}`}
            className="field-margin"
          >
            <Form.Label>Expiry Date</Form.Label>
            <Field name={expiryFieldName}>
              {({ field, meta, form }) => {
                const commitExpiryValue = isoValue => {
                  form.setFieldValue(field.name, isoValue);
                  if (process.env.NODE_ENV === 'development') {
                    logInfo(
                      'OrderItemKitComponentLotComponent',
                      'Committed kit lot expiry value',
                      {
                        orderItemIndex,
                        kitComponentIndex,
                        lotIndex,
                        expiryFieldName: field.name,
                        isoValue,
                      }
                    );
                  }
                };

                const commitManualExpiryInput = () => {
                  const parsedIso = parseManualDateToIso(expiryInputValue);
                  if (parsedIso === null) {
                    setExpiryInputValue(formatIsoToDisplay(field.value || ''));
                    setIsExpiryInputDirty(false);
                    if (process.env.NODE_ENV === 'development') {
                      logInfo(
                        'OrderItemKitComponentLotComponent',
                        'Rejected invalid manual kit lot expiry',
                        {
                          orderItemIndex,
                          kitComponentIndex,
                          lotIndex,
                          expiryFieldName: field.name,
                          typedValue: expiryInputValue,
                          persistedValue: field.value || '',
                        }
                      );
                    }
                    return;
                  }

                  commitExpiryValue(parsedIso);
                  setExpiryInputValue(formatIsoToDisplay(parsedIso));
                  setIsExpiryInputDirty(false);
                };

                return (
                  <DatePicker
                    selected={
                      !isExpiryInputDirty && field.value
                        ? new Date(field.value)
                        : null
                    }
                    value={expiryInputValue}
                    onChange={date => {
                      const isoDateValue = convertDateToIso(date);
                      commitExpiryValue(isoDateValue);
                      setExpiryInputValue(formatIsoToDisplay(isoDateValue));
                      setIsExpiryInputDirty(false);
                    }}
                    onChangeRaw={event => {
                      setIsExpiryInputDirty(true);
                      setExpiryInputValue(event.target.value);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitManualExpiryInput();
                      }
                    }}
                    onBlur={event => {
                      field.onBlur(event);
                      commitManualExpiryInput();
                    }}
                    onFocus={() => {
                      if (!isExpiryInputDirty) {
                        setExpiryInputValue(formatIsoToDisplay(field.value || ''));
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className={`form-control ${
                      meta.touched && !!meta.error
                        ? 'is-invalid'
                        : meta.touched && !meta.error && field.value !== ''
                        ? 'is-valid'
                        : ''
                    }`}
                    wrapperClassName="w-100"
                    inputClassName="text-center"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    todayButton="Today"
                  />
                );
              }}
            </Field>
            <Form.Control.Feedback type="invalid">
              {!!formik?.errors?.items?.[orderItemIndex]?.kitComponentLots?.[
                kitComponentIndex
              ]?.lots?.[lotIndex]?.expiry &&
                formik.errors.items?.[orderItemIndex]?.kitComponentLots?.[
                  kitComponentIndex
                ]?.lots?.[lotIndex]?.expiry}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          {/* Storage Location Field */}
          <Form.Group
            controlId={`kitComponentLocation${kitComponentIndex}${lotIndex}`}
            className="field-margin"
          >
            <Form.Label>Storage Location</Form.Label>
            <Field name={storageLocationFieldName}>
              {({ field, meta }) => (
                <FormControl
                  {...field}
                  type="text"
                  isValid={meta.touched && !meta.error && field.value !== ''}
                  isInvalid={meta.touched && !!meta.error}
                />
              )}
            </Field>
            <Form.Control.Feedback type="invalid">
              {!!formik?.errors?.items?.[orderItemIndex]?.kitComponentLots?.[
                kitComponentIndex
              ]?.lots?.[lotIndex]?.storageLocation &&
                formik.errors.items?.[orderItemIndex]?.kitComponentLots?.[
                  kitComponentIndex
                ]?.lots?.[lotIndex]?.storageLocation}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default OrderItemKitComponentLotComponent;
